import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { fmtDate } from "@/lib/brand";

const DEFAULT_STEPS = ["Check renewal requirements", "Gather documents", "Submit application / payment", "Receive renewed document"];

export function RenewDialog({ open, onClose, r, onDone }) {
  const inProgress = r.renewal?.in_progress;
  const [mode, setMode] = useState("complete");
  const [newDate, setNewDate] = useState("");
  const [notes, setNotes] = useState("");
  const [replace, setReplace] = useState(true);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMode(inProgress ? "complete" : "start"); setNewDate(""); setNotes(""); setFiles([]); } }, [open, inProgress]);

  const start = async () => {
    setBusy(true);
    try { await api.post(`/reminders/${r.id}/renewal/start`, { checklist: DEFAULT_STEPS }); toast.success("Renewal started"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const complete = async () => {
    if (!newDate) { toast.error("Enter the new verified date"); return; }
    setBusy(true);
    try {
      await api.post(`/reminders/${r.id}/renewal/complete`, { new_date: newDate, notes, replace_documents: replace });
      if (files.length) {
        const fd = new FormData();
        files.forEach((x) => fd.append("files", x));
        await api.post(`/reminders/${r.id}/attachments`, fd);
      }
      toast.success("Renewal recorded — previous date kept in history");
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="renew-dialog">
        <DialogHeader>
          <DialogTitle>Renew “{r.title}”</DialogTitle>
          <DialogDescription>Current date: {fmtDate(r.event_date)}. The previous date and files are kept in renewal history.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <button className="pm-tab" data-active={mode === "start"} onClick={() => setMode("start")} disabled={inProgress} data-testid="renew-mode-start">{inProgress ? "Renewal in progress" : "Start with checklist"}</button>
          <button className="pm-tab" data-active={mode === "complete"} onClick={() => setMode("complete")} data-testid="renew-mode-complete">Record new date</button>
        </div>
        {mode === "start" ? (
          <div>
            <p className="text-sm text-[var(--pm-muted)]">We'll track these steps. You can edit them on the Checklist tab.</p>
            <ul className="text-sm list-disc pl-5 my-3 space-y-1">{DEFAULT_STEPS.map((s) => <li key={s}>{s}</li>)}</ul>
            <button className="pm-btn pm-btn-primary w-full" onClick={start} disabled={busy} data-testid="renew-start-btn">Start renewal</button>
          </div>
        ) : (
          <div className="grid gap-4">
            <Field label="New verified date" required htmlFor="rn-date"><input id="rn-date" type="date" className="pm-input" value={newDate} min={r.event_date} onChange={(e) => setNewDate(e.target.value)} data-testid="renew-date-input" /></Field>
            <Field label="Notes" htmlFor="rn-notes"><input id="rn-notes" className="pm-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Renewed at embassy" data-testid="renew-notes-input" /></Field>
            <Field label="Upload the renewed document (optional)"><input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFiles(Array.from(e.target.files))} className="text-sm" data-testid="renew-file-input" /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-[var(--pm-primary)]" data-testid="renew-replace-checkbox" /> Move current files to previous versions</label>
            <button className="pm-btn pm-btn-primary" onClick={complete} disabled={busy} data-testid="renew-complete-btn">{busy ? "Saving…" : "Complete renewal"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
